// --- Currency ---
/** Валюта цен моделей. Отсутствует у модели → валюта провайдера → USD. */
export const CURRENCIES = ['RUB', 'USD', 'CNY'];
/** Проверка, что значение — допустимая валюта (RUB/USD/CNY). */
export function isCurrency(value) {
    return typeof value === 'string' && CURRENCIES.includes(value);
}
/** Ленивый разбор валюты: неизвестное значение → undefined. */
export function parseCurrency(value) {
    return isCurrency(value) ? value : undefined;
}
/**
 * Строгий разбор поля `currency`:
 * - `undefined` — поле не передано (сохранить existing);
 * - `null`/`''` — явно очистить (undefined);
 * - валидная валюта — принять;
 * - иная непустая строка — ошибка (`ok: false`), чтобы не терять значение молча.
 */
export function parseCurrencyInput(raw, existing) {
    if (raw === null || raw === '')
        return { ok: true, currency: undefined };
    if (raw === undefined)
        return { ok: true, currency: existing };
    return isCurrency(raw) ? { ok: true, currency: raw } : { ok: false };
}
/** Присутствуют ли в теле запроса ценовые поля (snake_case или camelCase). */
export function hasPriceInput(body) {
    if (!body || typeof body !== 'object')
        return false;
    const b = body;
    return 'input_price' in b || 'inputPrice' in b
        || 'output_price' in b || 'outputPrice' in b
        || 'cache_read_price' in b || 'cacheReadPrice' in b
        || 'cache_write_price' in b || 'cacheWritePrice' in b;
}
/** Канонические значения `cacheFormat` (для валидации входных данных API). */
export const CACHE_FORMATS = ['anthropic', 'alibaba'];
/** Проверка, что значение — допустимый формат inline-маркеров кэша. */
export function isCacheFormat(value) {
    return typeof value === 'string' && CACHE_FORMATS.includes(value);
}
/** Канонический набор нативных аудио-форматов (для валидации и UI). */
export const NATIVE_AUDIO_FORMATS = ['wav', 'mp3', 'ogg'];
/** Ключи контракта — для валидации входных данных API. */
export const MODEL_COMPATIBILITY_KEYS = [
    'reasoningField',
    'requireReasoning',
    'maxTokensField',
    'requireAssistantAfterTool',
    'streamUsage',
    'supportsToolChoice',
    'cacheFormat',
    'requireFinishReason',
    'supportsEffortUpdates',
    'supportsNativeVideo',
    'nativeAudioFormats',
];
/**
 * RouterAI — прокси с расширениями протоколов (нативное аудио/видео).
 *
 * Единственный носитель host-знания в слое совместимости: **эвристика**
 * умолчания для `supportsNativeVideo`/`nativeAudioFormats`. Сами значения
 * приходят данными (`compatibility`) — этот предикат лишь задаёт дефолт,
 * который любая запись каталога перекрывает явно (ADR-117).
 *
 * Функция чистая (только строка) — `node:*` в shared не тащим.
 */
export function isRouterAiUrl(url) {
    try {
        const host = new URL(url).hostname.toLowerCase();
        return host === 'routerai.ru' || host.endsWith('.routerai.ru');
    }
    catch {
        return false;
    }
}
/**
 * Нативный аудио-контейнер по MIME (ADR-115): `wav`/`mp3`/`ogg`.
 * Прочие MIME (в т.ч. не-аудио) → `null`.
 */
export function nativeAudioFormatForMime(mime) {
    const normalized = (mime ?? '').toLowerCase().split(';')[0].trim();
    if (normalized === 'audio/wav' || normalized === 'audio/x-wav' || normalized === 'audio/wave')
        return 'wav';
    if (normalized === 'audio/mpeg' || normalized === 'audio/mp3')
        return 'mp3';
    if (normalized === 'audio/ogg' || normalized === 'audio/opus' || normalized === 'audio/oga')
        return 'ogg';
    return null;
}
/**
 * Эвристики совместимости — те же, что в opencode
 * (`packages/ai/src/protocols/openai-chat.ts`):
 *
 *  - `requireReasoning`: тип/имя провайдера `deepseek`, URL ~ `deepseek.com`,
 *    модель ~ `deepseek`; провайдер `minimax` или URL ~ `minimax`
 *    (adaptive thinking у MiniMax идёт в тот же бюджет вывода);
 *  - `requireAssistantAfterTool`: Mistral-семейство (`mistral`, `devstral`,
 *    `codestral`, `pixtral`, `mixtral`);
 *  - `supportsToolChoice`: `false` для нативного Ollama (`type: 'ollama'`).
 *
 * `maxTokensField` намеренно **не** эвристируется: наш дефолт — `max_tokens`,
 * `max_completion_tokens` только при явном значении (локальные серверы).
 *
 * **Нативные медиа (ADR-115 × ADR-117).** Матрица «тип провайдера ×
 * RouterAI-совместимый хост» — дефолт для `nativeAudioFormats`/
 * `supportsNativeVideo`:
 *
 * | тип          | RouterAI | nativeAudioFormats | supportsNativeVideo |
 * |--------------|----------|--------------------|---------------------|
 * | openai/generic| да      | wav, mp3, ogg      | `true`              |
 * | openai/generic| нет     | wav, mp3           | `false`             |
 * | anthropic    | да       | wav                | `false`             |
 * | anthropic    | нет      | —                  | `false`             |
 * | прочие       | любой    | —                  | `false`             |
 *
 * Основание — прежнее поведение по хосту: `anthropic` принимает аудио только в
 * `wav` и только у RouterAI (Anthropic-совместимый шлюз на ogg/mp3 отвечает 200,
 * но модель аудио не слышит — такие форматы идут через aux-модель); OpenAI-протокол
 * принимает wav/mp3 всегда, ogg — расширение RouterAI; блок `video_url` знает
 * только RouterAI-совместимый OpenAI-протокол.
 *
 * Живой тест 2026-11-03: не-routerai прокси `itphx`
 * (`https://ai.itphx.ru/api/`) нативное видео **умеет** — старый предикат по
 * хосту давал ложноотрицательный ответ. Отсюда правило: дефолт по хосту —
 * лишь эвристика, а решение принимают **данные** (`compatibility`), которыми
 * провайдер/модель перекрывают эвристику явно.
 */
export function detectCompatibilityHeuristics(context) {
    const type = (context.providerType ?? '').toLowerCase();
    const name = (context.providerName ?? '').toLowerCase();
    const url = (context.baseUrl ?? '').toLowerCase();
    const model = (context.modelId ?? '').toLowerCase();
    const requireReasoning = type === 'deepseek'
        || name === 'deepseek'
        || url.includes('deepseek.com')
        || model.includes('deepseek')
        // MiniMax: adaptive thinking включён на стороне модели и расходует тот же
        // `max_tokens` — без запаса вывода ответ приходит пустым. Определяем по
        // провайдеру/URL, а не по модели: имя `minimax` носят сторонние видео-модели.
        || name === 'minimax'
        || url.includes('minimax');
    const mistral = ['mistral', 'devstral', 'codestral', 'pixtral', 'mixtral']
        .some((family) => model.includes(family));
    // ADR-115: нативные медиа — дефолт по типу протокола и RouterAI-совместимому
    // хосту (см. матрицу в докстринге). Явные значения `compatibility` перекрывают.
    const routerAi = isRouterAiUrl(context.baseUrl ?? '');
    const openAiLike = type === 'openai' || type === 'generic';
    const nativeAudioFormats = openAiLike
        ? (routerAi ? ['wav', 'mp3', 'ogg'] : ['wav', 'mp3'])
        : type === 'anthropic'
            ? (routerAi ? ['wav'] : [])
            : [];
    const supportsNativeVideo = openAiLike && routerAi;
    return {
        requireReasoning,
        requireAssistantAfterTool: mistral,
        supportsToolChoice: type !== 'ollama',
        nativeAudioFormats,
        supportsNativeVideo,
    };
}
/**
 * Наложение слоёв совместимости: `undefined`-поля не перетирают значение.
 * Поздние слои приоритетнее (провайдер → shorthand → модель).
 */
export function mergeModelCompatibility(...layers) {
    const merged = {};
    for (const layer of layers) {
        if (!layer)
            continue;
        for (const [key, value] of Object.entries(layer)) {
            if (value !== undefined)
                merged[key] = value;
        }
    }
    return merged;
}
/**
 * Разрешение итоговой совместимости.
 *
 * Порядок: **явное значение модели → явное значение провайдера → эвристика →
 * встроенный дефолт**. `reasoningField`, заданный явно на любом уровне, сам по
 * себе означает `requireReasoning: true` (как в opencode) — если
 * `requireReasoning` не переопределён явно (в т.ч. `false`).
 */
export function resolveModelCompatibility(providerCompat, modelCompat, heuristics = {}) {
    const explicitReasoningField = modelCompat?.reasoningField ?? providerCompat?.reasoningField;
    const explicitRequireReasoning = modelCompat?.requireReasoning ?? providerCompat?.requireReasoning;
    const requireReasoning = explicitRequireReasoning
        ?? (explicitReasoningField !== undefined ? true : (heuristics.requireReasoning ?? false));
    return {
        reasoningField: explicitReasoningField ?? heuristics.reasoningField ?? 'reasoning_content',
        requireReasoning,
        maxTokensField: modelCompat?.maxTokensField
            ?? providerCompat?.maxTokensField
            ?? heuristics.maxTokensField
            ?? 'max_tokens',
        requireAssistantAfterTool: modelCompat?.requireAssistantAfterTool
            ?? providerCompat?.requireAssistantAfterTool
            ?? heuristics.requireAssistantAfterTool
            ?? false,
        streamUsage: modelCompat?.streamUsage
            ?? providerCompat?.streamUsage
            ?? heuristics.streamUsage
            ?? true,
        supportsToolChoice: modelCompat?.supportsToolChoice
            ?? providerCompat?.supportsToolChoice
            ?? heuristics.supportsToolChoice
            ?? true,
        // Дефолт — `true` (как в opencode): стрим без `finish_reason` считается
        // недописанным. Явный `false` возвращает толерантное поведение.
        requireFinishReason: modelCompat?.requireFinishReason
            ?? providerCompat?.requireFinishReason
            ?? heuristics.requireFinishReason
            ?? true,
        // Effort-режим Anthropic — только явным значением (без авто-эвристики):
        // включение меняет тело запроса (output_config вместо thinking), и
        // угадывание по модели сломало бы шлюзы без поддержки поля.
        supportsEffortUpdates: modelCompat?.supportsEffortUpdates
            ?? providerCompat?.supportsEffortUpdates
            ?? heuristics.supportsEffortUpdates
            ?? false,
        // Inline-маркеры кэша — только явным значением: эвристики по типу
        // провайдера не ставятся, иначе менялось бы поведение существующих записей.
        cacheFormat: modelCompat?.cacheFormat ?? providerCompat?.cacheFormat ?? heuristics.cacheFormat,
        // ADR-115: нативные медиа — тот же порядок «модель → провайдер → эвристика».
        // Важно: `??` не схлопывает пустой массив (`[]` — не null/undefined), поэтому
        // явный пустой перечень форматов перекрывает эвристику («ни одного»).
        nativeAudioFormats: modelCompat?.nativeAudioFormats
            ?? providerCompat?.nativeAudioFormats
            ?? heuristics.nativeAudioFormats
            ?? [],
        supportsNativeVideo: modelCompat?.supportsNativeVideo
            ?? providerCompat?.supportsNativeVideo
            ?? heuristics.supportsNativeVideo
            ?? false,
    };
}
/**
 * Строгий разбор `compatibility` из тела API. Семантика — **замена**:
 * - `undefined` — поле не передано (сохранить existing);
 * - `null` — явно сбросить;
 * - объект — **заменить** значение целиком (UI присылает полный объект из
 *   черновика, поэтому отсутствующее в объекте поле означает «авто», а не
 *   «оставить как было»); пустой объект равнозначен сбросу;
 * - неизвестные поля и значения неверного типа — ошибка (не терять опечатки
 *   молча); иное — ошибка.
 */
export function parseModelCompatibilityInput(raw, existing) {
    if (raw === undefined)
        return { ok: true, value: existing };
    if (raw === null)
        return { ok: true, value: undefined };
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        return { ok: false, reason: 'compatibility должен быть объектом или null' };
    }
    const input = raw;
    for (const key of Object.keys(input)) {
        if (!MODEL_COMPATIBILITY_KEYS.includes(key)) {
            return { ok: false, reason: `compatibility: неизвестное поле «${key}»` };
        }
    }
    // Присланный объект — полное новое значение: прежние поля не наследуются.
    const replaced = {};
    if ('reasoningField' in input) {
        const value = input.reasoningField;
        if (typeof value !== 'string' || value.trim() === '') {
            return { ok: false, reason: 'compatibility.reasoningField должен быть непустой строкой' };
        }
        replaced.reasoningField = value.trim();
    }
    if ('maxTokensField' in input) {
        const value = input.maxTokensField;
        if (value !== 'max_tokens' && value !== 'max_completion_tokens') {
            return { ok: false, reason: 'compatibility.maxTokensField должен быть max_tokens или max_completion_tokens' };
        }
        replaced.maxTokensField = value;
    }
    if ('cacheFormat' in input) {
        const value = input.cacheFormat;
        if (!isCacheFormat(value)) {
            return { ok: false, reason: 'compatibility.cacheFormat должен быть anthropic или alibaba' };
        }
        replaced.cacheFormat = value;
    }
    for (const key of [
        'requireReasoning',
        'requireAssistantAfterTool',
        'streamUsage',
        'supportsToolChoice',
        'requireFinishReason',
        'supportsEffortUpdates',
        'supportsNativeVideo',
    ]) {
        if (!(key in input))
            continue;
        const value = input[key];
        if (typeof value !== 'boolean') {
            return { ok: false, reason: `compatibility.${key} должен быть boolean` };
        }
        replaced[key] = value;
    }
    if ('nativeAudioFormats' in input) {
        const value = input.nativeAudioFormats;
        if (!Array.isArray(value)) {
            return { ok: false, reason: 'compatibility.nativeAudioFormats должен быть массивом' };
        }
        const formats = [];
        for (const item of value) {
            if (typeof item !== 'string' || !NATIVE_AUDIO_FORMATS.includes(item)) {
                return {
                    ok: false,
                    reason: `compatibility.nativeAudioFormats: неизвестный формат «${String(item)}» (допустимы ${NATIVE_AUDIO_FORMATS.join(', ')})`,
                };
            }
            if (!formats.includes(item))
                formats.push(item);
        }
        // Пустой массив — валидное явное значение («ни одного формата»).
        replaced.nativeAudioFormats = formats;
    }
    return { ok: true, value: Object.keys(replaced).length > 0 ? replaced : undefined };
}
